import React from "react";

import "./style.scss";

export type formatterFunction = (labelText: string) => React.ReactNode;

export interface Props {
    /** The currently selected key of the given items */
    value: string;
    /** A function to be called when the user has clicked on a option button */
    onChange?: (nextKey: string) => void;
    /** Object with possible options */
    items: { [key: string]: string } | [string, string][];
    className?: string;
    /** This function can manipulate the content that is being put into the buttons. */
    labelFormatter?: formatterFunction;
}

const defaultFormatter: formatterFunction = (labelText: string) => labelText;

/**
 * The ButtonSelector is a simple component to select a key
 * from a key/value object. It will render a series of buttons
 * for each value in the object and highlight the button
 * whose key has been passed as `value`.
 *
 * Upon click of a button, the `onChange` function is called
 * and gets passed the next key string.
 *
 * Use the `labelFormatter` function to turn the default string labels into rich react components, if needed.
 */
export const ButtonSelector = ({ value, onChange, items, className, labelFormatter = defaultFormatter }: Props) => {
    const handleChange = (e: React.MouseEvent) => {
        const target = e.target as Element;
        const nextValue = target.getAttribute("data-key");
        if (onChange) {
            onChange(nextValue || "");
        }
    };
    const classNames = ["buttonSelector"];

    if (className) {
        classNames.push(className);
    }

    const entries = items instanceof Array ? items : Object.entries(items);

    return (
        <div className={classNames.join(" ")}>
            {entries.map(([key, label]) => (
                <button
                    key={key}
                    className={key === value ? "active" : undefined}
                    data-key={key}
                    onClick={handleChange}
                >
                    {labelFormatter(label)}
                </button>
            ))}
        </div>
    );
};
